import * as React from "react";
import {
  adminFetchMe,
  adminPreviewPath,
  adminSignIn,
  clearAdminSession,
  fetchAdminPresentationDetail,
  fetchAdminPresentations,
  formatAdminTime,
  formatAdminTimeShort,
  getPublicAppUrl,
  getStoredAccessToken,
  publicPresentationUrl,
  resetAdminPresentationEvents,
  type AdminPresentationDetail,
  type AdminPresentationRow,
} from "@/lib/adminApi";

export default function Admin() {
  const [booting, setBooting] = React.useState(true);
  const [email, setEmail] = React.useState("Alain.Onesti@elliman.com");
  const [password, setPassword] = React.useState("");
  const [userEmail, setUserEmail] = React.useState<string | null>(null);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [signingIn, setSigningIn] = React.useState(false);
  const [publicAppUrl, setPublicAppUrl] = React.useState("");

  const [rows, setRows] = React.useState<AdminPresentationRow[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [loadingList, setLoadingList] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<AdminPresentationDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [resetMessage, setResetMessage] = React.useState<string | null>(null);

  const loadList = React.useCallback(async () => {
    setLoadingList(true);
    setLoadError(null);
    try {
      const data = await fetchAdminPresentations();
      setRows(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load");
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const origin = await getPublicAppUrl();
      if (!cancelled) setPublicAppUrl(origin);
      if (!getStoredAccessToken()) {
        if (!cancelled) setBooting(false);
        return;
      }
      try {
        const me = await adminFetchMe();
        if (!cancelled) {
          setUserEmail(me.email);
          await loadList();
        }
      } catch {
        clearAdminSession();
        if (!cancelled) setUserEmail(null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList]);

  React.useEffect(() => {
    if (!selectedId || !userEmail) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setResetMessage(null);
    fetchAdminPresentationDetail(selectedId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, userEmail]);

  const onSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setAuthError(null);
    try {
      const me = await adminSignIn(email, password);
      setUserEmail(me.email);
      setPassword("");
      await loadList();
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : "Sign in failed");
      setUserEmail(null);
    } finally {
      setSigningIn(false);
    }
  };

  const onSignOut = () => {
    clearAdminSession();
    setUserEmail(null);
    setRows([]);
    setSelectedId(null);
    setDetail(null);
  };

  const onResetAnalytics = async () => {
    if (!detail) return;
    const ok = window.confirm(
      `Reset analytics for ${detail.owner_name} — ${detail.property}?\n\nThis clears page views and CTA events only. The presentation itself is not deleted.`
    );
    if (!ok) return;
    setResetting(true);
    setResetMessage(null);
    try {
      const result = await resetAdminPresentationEvents(detail.id);
      setResetMessage(`Cleared ${result.deleted} event${result.deleted === 1 ? "" : "s"}.`);
      const refreshed = await fetchAdminPresentationDetail(detail.id);
      setDetail(refreshed);
      await loadList();
    } catch (err) {
      setResetMessage(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.owner_name || "").toLowerCase().includes(q) ||
        (r.property || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  if (booting) {
    return (
      <div className="min-h-screen bg-[#f4f3ef] text-charcoal" data-testid="admin-boot">
        <div className="mx-auto max-w-6xl px-4 py-16 text-sm text-charcoal/60">Loading…</div>
      </div>
    );
  }

  if (!userEmail) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#f4f3ef] px-4 text-charcoal"
        data-testid="admin-login"
      >
        <form
          onSubmit={onSignIn}
          className="w-full max-w-sm border border-charcoal/10 bg-white p-8 shadow-sm"
        >
          <div className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-charcoal/55">
            PitchAI
          </div>
          <h1 className="mt-2 font-serif text-3xl text-charcoal">Admin</h1>
          <p className="mt-2 text-sm text-charcoal/60">Sign in to view presentation opens.</p>

          <label className="mt-8 block text-xs uppercase tracking-[0.14em] text-charcoal/55">
            Email
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border border-charcoal/15 bg-white px-3 py-2.5 text-sm text-charcoal outline-none focus:border-charcoal/40"
              data-testid="admin-email"
            />
          </label>

          <label className="mt-4 block text-xs uppercase tracking-[0.14em] text-charcoal/55">
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border border-charcoal/15 bg-white px-3 py-2.5 text-sm text-charcoal outline-none focus:border-charcoal/40"
              data-testid="admin-password"
            />
          </label>

          {authError && (
            <p className="mt-4 text-sm text-red-700" data-testid="admin-auth-error">
              {authError}
            </p>
          )}

          <button
            type="submit"
            disabled={signingIn}
            className="mt-6 w-full bg-charcoal px-4 py-3 text-xs font-medium uppercase tracking-[0.16em] text-ivory disabled:opacity-60"
            data-testid="admin-signin"
          >
            {signingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f3ef] text-charcoal" data-testid="admin-dashboard">
      <header className="border-b border-charcoal/10 bg-white">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div>
            <div className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-charcoal/55">
              PitchAI Admin
            </div>
            <h1 className="mt-1 font-serif text-2xl md:text-3xl">Presentation opens</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-charcoal/55">{userEmail}</span>
            <a
              href="/create"
              className="bg-charcoal px-3 py-1.5 text-xs uppercase tracking-[0.12em] text-ivory"
              data-testid="admin-new-presentation"
            >
              New Presentation
            </a>
            <button
              type="button"
              onClick={() => loadList()}
              className="border border-charcoal/15 px-3 py-1.5 text-xs uppercase tracking-[0.12em] hover:border-charcoal/40"
              data-testid="admin-refresh"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="border border-charcoal/15 px-3 py-1.5 text-xs uppercase tracking-[0.12em] hover:border-charcoal/40"
              data-testid="admin-signout"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-5 md:px-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="search"
            placeholder="Search owner or property"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md border border-charcoal/15 bg-white px-3 py-2 text-sm outline-none focus:border-charcoal/40"
            data-testid="admin-search"
          />
          <div className="text-xs text-charcoal/50">
            {loadingList ? "Loading…" : `${filtered.length} presentation${filtered.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {loadError && (
          <p className="mb-4 text-sm text-red-700" data-testid="admin-load-error">
            {loadError}
          </p>
        )}

        <div className="overflow-x-auto border border-charcoal/10 bg-white">
          <table className="min-w-full text-left text-sm" data-testid="admin-table">
            <thead className="border-b border-charcoal/10 bg-[#faf9f6] text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/55">
              <tr>
                <th className="px-3 py-3 font-medium">Owner</th>
                <th className="px-3 py-3 font-medium">Property</th>
                <th className="px-3 py-3 font-medium">Opened</th>
                <th className="px-3 py-3 font-medium">Views</th>
                <th className="px-3 py-3 font-medium">First open</th>
                <th className="px-3 py-3 font-medium">Last open</th>
                <th className="px-3 py-3 font-medium">CTA</th>
                <th className="px-3 py-3 font-medium">Presentation</th>
                <th className="px-3 py-3 font-medium">Generated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelectedId(row.id)}
                  className={`cursor-pointer border-b border-charcoal/5 hover:bg-[#faf9f6] ${
                    selectedId === row.id ? "bg-[#f7f4ec]" : ""
                  }`}
                  data-testid={`admin-row-${row.slug}`}
                >
                  <td className="px-3 py-3 font-medium">{row.owner_name}</td>
                  <td className="max-w-[220px] px-3 py-3 text-charcoal/80">{row.property}</td>
                  <td className="px-3 py-3">
                    <span
                      className={
                        row.opened
                          ? "inline-block rounded-sm bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800"
                          : "inline-block rounded-sm bg-charcoal/5 px-2 py-0.5 text-xs font-medium text-charcoal/55"
                      }
                    >
                      {row.opened ? "Opened" : "Not opened"}
                    </span>
                  </td>
                  <td className="px-3 py-3 tabular-nums">
                    {row.views}
                    {row.unique_sessions > 0 && (
                      <span className="ml-1 text-xs text-charcoal/40">
                        ({row.unique_sessions} sess)
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-charcoal/75">
                    {formatAdminTime(row.first_opened_at)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-charcoal/75">
                    {formatAdminTime(row.last_opened_at)}
                  </td>
                  <td className="px-3 py-3 text-charcoal/75">
                    {row.cta_labels?.length ? row.cta_labels.join(" · ") : "—"}
                  </td>
                  <td className="px-3 py-3">
                    {row.slug ? (
                      <a
                        href={adminPreviewPath(row.slug, publicAppUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-medium uppercase tracking-[0.12em] text-charcoal underline-offset-2 hover:underline"
                        data-testid={`admin-open-${row.slug}`}
                      >
                        Open
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-charcoal/75">
                    {formatAdminTime(row.created_at)}
                  </td>
                </tr>
              ))}
              {!loadingList && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-10 text-center text-charcoal/50">
                    No presentations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {selectedId && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-charcoal/30"
          data-testid="admin-detail-overlay"
          onClick={() => setSelectedId(null)}
        >
          <aside
            className="h-full w-full max-w-md overflow-y-auto bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-detail"
          >
            <div className="sticky top-0 flex items-center justify-between border-b border-charcoal/10 bg-white px-5 py-4">
              <h2 className="font-serif text-xl">Detail</h2>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="text-xs uppercase tracking-[0.12em] text-charcoal/55 hover:text-charcoal"
              >
                Close
              </button>
            </div>

            {detailLoading && (
              <p className="px-5 py-6 text-sm text-charcoal/50">Loading…</p>
            )}

            {!detailLoading && detail && (
              <div className="space-y-5 px-5 py-5 text-sm">
                <div>
                  <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                    Owner
                  </div>
                  <div className="mt-1 font-medium">{detail.owner_name}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                    Property
                  </div>
                  <div className="mt-1">{detail.property}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                    Presentation URL
                  </div>
                  <div className="mt-1 break-all text-charcoal/80">
                    {detail.presentation_url ||
                      publicPresentationUrl(detail.slug, publicAppUrl) ||
                      detail.presentation_path}
                  </div>
                  {detail.slug && (
                    <a
                      href={adminPreviewPath(detail.slug, publicAppUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-xs font-medium uppercase tracking-[0.12em] underline-offset-2 hover:underline"
                      data-testid="admin-detail-open-preview"
                    >
                      Open Presentation
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                      Generated
                    </div>
                    <div className="mt-1">{formatAdminTime(detail.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                      Opened
                    </div>
                    <div className="mt-1">{detail.opened ? "Yes" : "No"}</div>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                      Total views
                    </div>
                    <div className="mt-1 tabular-nums">{detail.views}</div>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                      Unique sessions
                    </div>
                    <div className="mt-1 tabular-nums">{detail.unique_sessions}</div>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                      First opened
                    </div>
                    <div className="mt-1">{formatAdminTime(detail.first_opened_at)}</div>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                      Last opened
                    </div>
                    <div className="mt-1">{formatAdminTime(detail.last_opened_at)}</div>
                  </div>
                </div>

                <div>
                  <div className="mb-3 text-[0.65rem] uppercase tracking-[0.12em] text-charcoal/45">
                    Activity
                  </div>
                  {detail.activity?.length ? (
                    <ul className="space-y-2 border-t border-charcoal/10 pt-3" data-testid="admin-activity">
                      {detail.activity.map((item, idx) => (
                        <li
                          key={item.id || `${item.event_type}-${idx}`}
                          className="flex gap-3 text-sm"
                        >
                          <span className="w-20 shrink-0 tabular-nums text-charcoal/50">
                            {formatAdminTimeShort(item.created_at)}
                          </span>
                          <span>{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-charcoal/45">No activity yet</p>
                  )}
                </div>

                <div className="border-t border-charcoal/10 pt-5">
                  <button
                    type="button"
                    onClick={onResetAnalytics}
                    disabled={resetting}
                    className="border border-charcoal/20 px-3 py-2 text-xs uppercase tracking-[0.12em] text-charcoal/70 hover:border-charcoal/40 hover:text-charcoal disabled:opacity-50"
                    data-testid="admin-reset-analytics"
                  >
                    {resetting ? "Resetting…" : "Reset analytics"}
                  </button>
                  {resetMessage && (
                    <p className="mt-2 text-xs text-charcoal/55" data-testid="admin-reset-message">
                      {resetMessage}
                    </p>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
